import { useEffect, useRef, useState, useMemo } from 'react'
import PropTypes from 'prop-types'
import { format, fromZonedTime } from 'date-fns-tz'
import { useTranslation } from 'react-i18next'

import api from '../../../../api/api'
import { dayMinutesToHoursMinutes, getMinutesofDayFromDate } from '../../../../helpers/helpers'
import { selectTimeRange, getReservedTimeRanges } from '../helpers'

const TimeSelectView = ({
  amenity,
  selectedDate,
  timezone,
  value,
  showAllDates,
  onChange = () => {},
  onBack = () => {},
  setHasConflicts = () => {},
  setReservedTimeRanges = () => {},
  locationId,
}) => {
  const { t } = useTranslation(['client-reservations'])
  const timeout = useRef(null)
  const [timeSlots, setTimeSlots] = useState([])
  const [loadingTimeScopes, setLoadingTimeScopes] = useState(true)

  const onBackClick = () => {
    onChange(null)
    setHasConflicts(false)
    onBack()
  }

  const fetchData = async () => {
    try {
      const responseData = await api.getTimeScopes(
        amenity.id,
        fromZonedTime(selectedDate.setHours(0, 0, 0, 0), timezone).getTime(),
        locationId,
      )
      if (format(new Date(), 'yyyy-MM-dd') === selectedDate) {
        const actualTime = getMinutesofDayFromDate(new Date())
        const availableTimeScopes = responseData.filter((data) => data.startTime > actualTime)
        setTimeSlots(availableTimeScopes)
      } else {
        setTimeSlots(responseData)
      }
    } catch ({ message }) {
      console.error(message)
    } finally {
      setLoadingTimeScopes(false)
    }
  }

  useEffect(() => {
    fetchData()
    return () => {
      if (timeout.current) clearTimeout(timeout.current)
    }
  }, [])

  const slotsDisabled = useMemo(
    () =>
      timeSlots
        .filter(
          ({ reservations, isBlocked, locations = [] }) =>
            reservations >= amenity.capacity ||
            isBlocked ||
            locations.some((location) => location.id === locationId && location.capacity <= 0),
        )
        .map(({ startTime }) => startTime),
    [timeSlots, amenity, locationId],
  )

  const onTimePick = ({ startTime, endTime }) => {
    if (startTime && endTime) {
      const reserved = getReservedTimeRanges(startTime, endTime, slotsDisabled)
      setReservedTimeRanges(reserved)
      const isEndTimeAllowed = reserved[0] === endTime
      setHasConflicts(reserved.length > 0 && !isEndTimeAllowed)
    }
    onChange({ startTime, endTime })
  }

  const onClickTimeSlot = (startTime) => {
    const time = selectTimeRange({
      timeSlot: startTime,
      selectedTime: value,
      slotsDisabled: showAllDates ? [] : slotsDisabled,
    })

    onTimePick(time)
  }

  const renderTimeSlots = () =>
    timeSlots.map((timeSlot) => {
      const time = timeSlot.startTime
      const isRangeSelected =
        value && typeof value.startTime === 'number' && typeof value.endTime === 'number'
      const selected =
        value &&
        (value.startTime === time ||
          value.endTime === time ||
          (isRangeSelected && time > value.startTime && time < value.endTime))
      const disabled = !showAllDates && slotsDisabled.includes(time)

      return (
        <div
          key={timeSlot.startTime}
          onClick={() => !disabled && onClickTimeSlot(time)}
          className={`reservation-time-selection__timeslot ${
            disabled ? 'reservation-time-selection__timeslot-disabled' : ''
          } ${selected ? 'reservation-time-selection__timeslot-selected' : ''}`}
        >
          {dayMinutesToHoursMinutes(time)}
        </div>
      )
    })

  return (
    <div className={`reservation-time-selection ${loadingTimeScopes && 'is-loading'}`}>
      <div className="reservation-time-selection__calendar">
        <div className="reservation-time-selection__header">
          <button
            className="reservation-time-selection__header-back-button "
            onClick={onBackClick}
            type="button"
          >
            {'<'}
          </button>
          <span className="reservation-time-selection__header-title">
            {format(selectedDate, 'dd MMMM yyyy')}
          </span>
          <span />
        </div>
        <div className="reservation-time-selection__timeslots">
          {!!timeSlots && !!timeSlots.length ? (
            renderTimeSlots()
          ) : (
            <div className="no-time-scopes">
              {t('client-reservations:timeSelect.noTimeSlotsAvailable')}
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

TimeSelectView.propTypes = {
  amenity: PropTypes.object,
  selectedDate: PropTypes.object,
  value: PropTypes.object,
  timezone: PropTypes.string,
  showAllDates: PropTypes.bool,
  onChange: PropTypes.func,
  onBack: PropTypes.func,
  setHasConflicts: PropTypes.func,
  setReservedTimeRanges: PropTypes.func,
  locationId: PropTypes.number,
}

export default TimeSelectView
